"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useGetReviews, useGetTestimonials } from "@/actions/testimonial";
import TestimonialCard from "./TestimonialCard";
import AvisCard from "./AvisCard";
import Pagination from "../Common/Pagination";
import Button from "../ui/Button";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

type TabId = "Avis" | "Temoignages";

const TABS: { id: TabId; label: string }[] = [
  { id: "Avis", label: "Les Avis" },
  { id: "Temoignages", label: "Les Témoignages" },
];

const TEMOIGNAGES_PER_PAGE = 3;

const paginate = <T,>(items: T[], page: number, size: number): T[] =>
  items.slice((page - 1) * size, page * size);

const stripNotePrefix = (date = "") => date.replace(/^Noté le\s*/i, "");

// ---------------------------------------------------------------------------
// BackButton
// ---------------------------------------------------------------------------

const BackButton: React.FC = () => {
  const router = useRouter();

  return (
    <div className="relative">
      <Button
        variant="outline-gradient"
        onClick={() => router.back()}
        className="absolute left-0 -top-10 h-9 w-25 rounded-lg text-[14px]! md:top-0 md:w-30.25 md:h-9 md:text-[15px]"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className="fill-[#FC7F16] group-hover:fill-white"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15 8C15 8.47339 14.6082 8.85714 14.125 8.85714L3.98744 8.85714L7.74372 12.5368C8.08543 12.8715 8.08543 13.4142 7.74372 13.7489C7.40201 14.0837 6.84799 14.0837 6.50628 13.7489L1.25628 8.60609C0.914573 8.27136 0.914573 7.72864 1.25628 7.39391L6.50628 2.25105C6.84799 1.91632 7.40201 1.91632 7.74372 2.25105C8.08543 2.58579 8.08543 3.1285 7.74372 3.46323L3.98744 7.14286L14.125 7.14286C14.6082 7.14286 15 7.52661 15 8Z"
          />
        </svg>
        Retour
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TabBar
// ---------------------------------------------------------------------------

interface TabBarProps {
  activeTab: TabId;
  onChange: (id: TabId) => void;
}

const TabBar: React.FC<TabBarProps> = ({ activeTab, onChange }) => (
  <div className="mb-9.25 flex flex-wrap justify-center gap-15 sm:gap-10">
    {TABS.map((tab) => (
      <button
        key={tab.id}
        type="button"
        onClick={() => onChange(tab.id)}
        aria-pressed={activeTab === tab.id}
        className={`h-6.5 cursor-pointer rounded-3xl border border-[#EFEFEF] px-4 text-[10px] leading-3.5 transition sm:h-11.5 sm:px-6 sm:text-[16px] ${
          activeTab === tab.id
            ? "bg-[#1B365E] text-white"
            : "bg-white text-[#1B365E] hover:bg-[#1B365E]/10"
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// State helpers (loading / error / empty)
// ---------------------------------------------------------------------------

const AvisSkeleton: React.FC = () => (
  <div className="mx-auto mt-8 flex max-w-192.75 flex-col gap-9 md:mt-12">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="h-40 w-full animate-pulse rounded-2xl border border-[#EFEFEF] bg-gray-100"
      />
    ))}
  </div>
);

const TemoignageSkeleton: React.FC = () => (
  <div className="mx-auto mt-8 flex max-w-7xl flex-col gap-6 md:mt-12 md:gap-8">
    {Array.from({ length: 2 }).map((_, i) => (
      <div key={i} className="h-56 w-full animate-pulse rounded-3xl bg-gray-200" />
    ))}
  </div>
);

const StateMessage: React.FC<{ tone?: "muted" | "error"; children: React.ReactNode }> = ({
  tone = "muted",
  children,
}) => (
  <p
    className={`mt-12 text-center text-sm ${tone === "error" ? "text-red-500" : "text-[#676767]"}`}
  >
    {children}
  </p>
);

// ---------------------------------------------------------------------------
// AllTemoignages — orchestrator
// ---------------------------------------------------------------------------

const AllTemoignages: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>("Avis");
  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  const isAvis = activeTab === "Avis";

  // Reviews are paginated server-side; testimonials are few and paginated client-side.
  const {
    reviews,
    reviewsMeta,
    reviewsLoading,
    reviewsError,
    reviewsEmpty,
  } = useGetReviews(isAvis ? page : 1);

  const {
    testimonials,
    testimonialsLoading,
    testimonialsError,
    testimonialsEmpty,
  } = useGetTestimonials();

  // Per-tab derived view state.
  const loading = isAvis ? reviewsLoading : testimonialsLoading;
  const error = isAvis ? reviewsError : testimonialsError;
  const empty = isAvis ? reviewsEmpty : testimonialsEmpty;

  const totalItems = isAvis
    ? reviewsMeta?.total ?? 0
    : testimonials.length;
  const totalPages = isAvis
    ? reviewsMeta?.last_page ?? 1
    : Math.max(1, Math.ceil(testimonials.length / TEMOIGNAGES_PER_PAGE));

  const heading = isAvis ? "Tous les avis" : "Tous les Témoignages";
  const latestDate = stripNotePrefix(
    isAvis ? reviews[0]?.date : testimonials[0]?.date,
  );

  const visibleTestimonials = paginate(testimonials, page, TEMOIGNAGES_PER_PAGE);

  const changeTab = (id: TabId) => {
    if (id === activeTab) return;
    setActiveTab(id);
    setPage(1);
  };

  const goToPage = (next: number) => {
    const clamped = Math.min(Math.max(next, 1), totalPages);
    if (clamped === page) return;
    setPage(clamped);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="px-4 py-10 md:py-14">
      <BackButton />
      <TabBar activeTab={activeTab} onChange={changeTab} />

      <h2 className="text-center font-red-hat text-[30px] font-light leading-10 tracking-[0.2px] text-[#1B365E] sm:text-[40px] sm:leading-13.25">
        {heading}
      </h2>

      {/* Summary bar: evaluation badge + latest-reviews count */}
      <div className="mt-6 flex flex-col items-center gap-2 sm:flex-row sm:justify-center md:mt-8">
        <Image
          src="/assets/icons/Evalutation-v2.svg"
          alt="Excellente évaluation de nos formations"
          title="Excellente évaluation de nos formations"
          width={307}
          height={73}
          className="h-18.25 w-full max-w-76.75"
        />
        <div className="min-h-18.5 rounded-xl border border-[#EFEFEF] px-5 py-4.25 text-center sm:text-left">
          <p className="whitespace-nowrap text-[14px] font-light leading-5 text-[#676767]">
            Derniers avis {latestDate ? `(voir : ${latestDate})` : ""}
          </p>
          <p className="text-[14px] font-bold leading-5 text-[#1B365E]">
            {totalItems} {isAvis ? "avis" : "témoignages"}
          </p>
        </div>
      </div>

      {/* List */}
      <div ref={listRef}>
        {error ? (
          <StateMessage tone="error">
            Une erreur est survenue lors du chargement. Veuillez réessayer.
          </StateMessage>
        ) : loading ? (
          isAvis ? <AvisSkeleton /> : <TemoignageSkeleton />
        ) : empty ? (
          <StateMessage>
            {isAvis
              ? "Aucun avis pour le moment."
              : "Aucun témoignage pour le moment."}
          </StateMessage>
        ) : isAvis ? (
          <div className="mx-auto mt-8 flex max-w-192.75 flex-col gap-9 md:mt-12">
            {reviews.map((avis) => (
              <AvisCard key={avis.id} avis={avis} />
            ))}
          </div>
        ) : (
          <div className="mx-auto mt-8 flex max-w-7xl flex-col gap-6 md:mt-12 md:gap-8">
            {visibleTestimonials.map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                tone={index % 2 === 0 ? "navy" : "dark"}
              />
            ))}
          </div>
        )}
      </div>

      {!loading && !error && !empty && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={goToPage}
          className="mt-10 md:mt-14"
        />
      )}
    </section>
  );
};

export default AllTemoignages;
